import { trackBeginCheckout } from "@/lib/analytics";
import { useId, useMemo, useState, type ReactNode } from "react";
import { Crown, Clock, CalendarPlus, Check, Loader2, ArrowLeft } from "lucide-react";
import SessionBookingFlow from "@/components/panel/SessionBookingFlow";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useMentoringWallet } from "@/hooks/useMentoringWallet";
import { type VipTierKey } from "@/hooks/useVipPricing";
import { useVipPricing } from "@/hooks/useVipPricing";
import { useHasActivePanel } from "@/hooks/useHasActivePanel";
import { startMentoringCheckout } from "@/lib/mentoringCheckout";
import { useLanguage } from "@/i18n/LanguageContext";

export interface VipMentoringPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lang: "es" | "en";
  /** Minutes still available in the wallet. */
  minutesRemaining?: number | undefined;
  /** Minutes granted for the current cycle. */
  minutesTotal?: number | undefined;
  onBook?: (() => void) | undefined;
  /** Trigger element rendered as the popover anchor. */
  children?: ReactNode;
}

/** Maps the wallet plan_key to one of the three mentoring tiers. */
const tierFromPlanKey = (planKey: string): VipTierKey | null => {
  const k = (planKey || "").toLowerCase();
  if (k.includes("arranque") || k.includes("brujula")) return "arranque";
  if (k.includes("crucero") || k.includes("sherpa")) return "crucero";
  if (k.includes("partner") || k.includes("fractional")) return "partner";
  return null;
};

/** One-off hour prices in cents — must mirror PRICE_BY_HOURS in
 *  supabase/functions/create-extra-hours-checkout/index.ts (180€ / 320€). */
const EXTRA_HOUR_PRICES: Record<1 | 2, number> = { 1: 18000, 2: 32000 };

/** Small (i) button with its own popover: hover on desktop, tap on touch, keyboard accessible. */
const InfoTip = ({ label, text }: { label: string; text: string }) => {
  const [open, setOpen] = useState(false);
  const id = useId();
  const hoverable = () => typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-describedby={open ? id : undefined}
          aria-expanded={open}
          onMouseEnter={() => hoverable() && setOpen(true)}
          onMouseLeave={() => hoverable() && setOpen(false)}
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
          className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border bg-card font-body text-[10px] font-semibold normal-case leading-none tracking-normal text-muted-foreground transition-colors before:absolute before:-inset-3 before:content-[''] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          i
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={id}
        role="tooltip"
        side="top"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={() => hoverable() && setOpen(true)}
        onMouseLeave={() => hoverable() && setOpen(false)}
        className="z-[60] w-auto max-w-[260px] rounded-xl border border-border bg-card p-3 font-body text-[13px] font-normal normal-case leading-snug tracking-normal text-foreground shadow-md"
      >
        {text}
      </PopoverContent>
    </Popover>
  );
};

const VipMentoringPanel = ({
  open,
  onOpenChange,
  lang,
  minutesRemaining = 0,
  minutesTotal = 0,
  onBook,
  children,
}: VipMentoringPanelProps) => {
  const { t } = useLanguage();
  const es = lang === "es";
  const { wallet } = useMentoringWallet();
  const { hasPanel, isAdmin } = useHasActivePanel();
  const { tiers } = useVipPricing(es, "addon");

  const [view, setView] = useState<"summary" | "booking">("summary");
  const [buyingHours, setBuyingHours] = useState<1 | 2 | null>(null);
  const [subscribing, setSubscribing] = useState<VipTierKey | null>(null);
  /** Explicitly picked tier in the popup (null = follow the active plan / default). */
  const [userPick, setUserPick] = useState<VipTierKey | null>(null);

  const remaining = Math.max(0, minutesRemaining) / 60;
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", es ? "," : "."));

  const activeTier = useMemo(() => tierFromPlanKey(wallet?.planKey ?? ""), [wallet?.planKey]);

  const plans: { key: VipTierKey; name: string; price: string; period: string; cadence: string }[] = [
    {
      key: "arranque",
      name: es ? "Quincenal" : "Biweekly",
      price: tiers.arranque.monthly.label,
      period: es ? "/mes" : "/mo",
      cadence: es ? "1 sesión cada 15 días" : "1 session every 15 days",
    },
    {
      key: "crucero",
      name: es ? "Semanal" : "Weekly",
      price: tiers.crucero.monthly.label,
      period: es ? "/mes" : "/mo",
      cadence: es ? "1 sesión a la semana" : "1 session per week",
    },
    {
      key: "partner",
      name: es ? "Intensivo" : "Intensive",
      price: tiers.partner.monthly.label,
      period: es ? "/mes" : "/mo",
      cadence: es ? "2 sesiones a la semana" : "2 sessions per week",
    },
  ];

  const canBook = remaining > 0;
  const selected: VipTierKey = userPick ?? activeTier ?? "arranque";

  const buyExtraHours = async (h: 1 | 2) => {
    setBuyingHours(h);
    try {
      const { data, error } = await supabase.functions.invoke("create-extra-hours-checkout", {
        body: { hours: h },
      });
      if (error) throw error;
      if (data?.url) {
        trackBeginCheckout({ source: "mentoria_vip", productType: "extra_hours", productKey: `${h}h`, interval: "one_time" });
        window.open(data.url, "_blank");
      } else throw new Error("no_url");
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || (es ? "No se pudo iniciar el pago" : "Could not start checkout"),
        variant: "destructive",
      });
    } finally {
      setBuyingHours(null);
    }
  };

  const subscribeTier = async (tier: VipTierKey) => {
    setSubscribing(tier);
    try {
      const res = await startMentoringCheckout({
        hasPanel: hasPanel || isAdmin,
        tierKey: tier,
        interval: "month",
      });
      if (res.url) {
        window.open(res.url, "_blank");
      } else if (res.success) {
        toast({
          title: es ? "Mentoría activada" : "Mentoring activated",
          description: es
            ? "Tu plan se ha actualizado. En unos segundos verás las horas nuevas."
            : "Your plan is updated. Your new hours will appear in a few seconds.",
        });
      } else {
        throw new Error(res.error || "checkout_error");
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || (es ? "No se pudo iniciar la contratación" : "Could not start checkout"),
        variant: "destructive",
      });
    } finally {
      setSubscribing(null);
    }
  };

  // Hours breakdown: plan (monthly, renews) vs purchased consultations (never expire)
  const extraRemaining = Math.max(0, (wallet?.extraMinutes ?? 0) - (wallet?.extraUsed ?? 0)) / 60;
  const planRemaining = Math.max(
    0,
    (wallet?.planMinutes ?? 0) - (wallet?.planUsed ?? 0) + (wallet?.rolloverMinutes ?? 0) - (wallet?.rolloverUsed ?? 0),
  ) / 60;

  const booking = view === "booking";

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) setView("summary");
        onOpenChange(next);
      }}
    >
      {children ? <PopoverTrigger asChild>{children}</PopoverTrigger> : null}
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className={cn(
          "z-50 flex max-h-[76vh] flex-col overflow-hidden rounded-2xl border border-border bg-popover p-0 shadow-xl",
          booking ? "w-[min(400px,calc(100vw-2rem))]" : "w-[min(340px,calc(100vw-2rem))]",
        )}
      >
        {booking ? (
          <>
            <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3.5 py-3">
              <button
                type="button"
                onClick={() => setView("summary")}
                aria-label={es ? "Volver" : "Back"}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
              </button>
              <p className="min-w-0 flex-1 truncate font-display text-[15px] font-semibold">
                {es ? "Reservar cita" : "Book a session"}
              </p>
            </div>
            <SessionBookingFlow
              active={open}
              variant="inline"
              onClose={() => {
                setView("summary");
                onOpenChange(false);
              }}
            />
          </>
        ) : (
          <div className="max-h-[76vh] overflow-y-auto p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-foreground" aria-hidden="true" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-bold text-foreground">{t("expertSupport", "title")}</h3>
            </div>

            {/* One-off hours */}
            <div className="rounded-2xl border border-border bg-card p-3.5">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-body text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={1.5} />
                  {t("expertSupport", "oneOffHours")}
                  <InfoTip
                    label={es ? "Más información" : "More information"}
                    text={es
                      ? "Para una duda concreta o un encargo cerrado. Pagas solo lo que usas, las horas no caducan y no hay permanencia. Sirve si aún no sabes si necesitas un seguimiento."
                      : "For a specific question or a closed task. You only pay for what you use, hours never expire and there is no commitment. Useful if you are not yet sure you need ongoing support."}
                  />
                </span>
                {extraRemaining > 0 && (
                  <span className="font-figures tabular-nums text-xl font-bold text-foreground">{fmt(extraRemaining)} h</span>
                )}
              </div>
              <p className="mt-1 font-body text-[11px] text-muted-foreground">
                {t("expertSupport", "neverExpire")}
              </p>
              {activeTier && planRemaining > 0 && (
                <div className="mt-2.5 border-t border-border pt-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-body text-[11px] font-semibold text-foreground">
                      {t("expertSupport", "monthlyMentoring")}
                    </span>
                    <span className="font-body text-[11px] text-muted-foreground">
                      <span className="font-semibold text-foreground">{fmt(planRemaining)} h</span>
                      {" · "}{t("expertSupport", "renewsOnFirst")}
                    </span>
                  </div>
                </div>
              )}
              <div className="mt-2.5 grid grid-cols-2 gap-2">
                {([1, 2] as const).map((h) => (
                  <button
                    key={h}
                    type="button"
                    disabled={buyingHours !== null}
                    onClick={() => buyExtraHours(h)}
                    className={cn(
                      "flex items-center justify-between gap-2 rounded-full border border-border bg-background px-3 py-2 font-body text-[12px] font-medium text-foreground transition-colors hover:bg-secondary",
                      buyingHours !== null && "opacity-60",
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      {buyingHours === h && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                      {h === 1 ? t("expertSupport", "oneHour") : t("expertSupport", "twoHours")}
                    </span>
                    <span className="font-display text-[13px] font-bold">
                      {EXTRA_HOUR_PRICES[h] / 100}€
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Monthly mentoring plans — always visible */}
            <div>
              <p className="flex items-center gap-1.5 font-body text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {es ? "Mentoría" : "Mentoring"}
                <InfoTip
                  label={es ? "Más información" : "More information"}
                  text={es
                    ? "Acompañamiento periódico: el mismo criterio cada 15 días o cada semana, con continuidad entre sesiones. Mejor si el objetivo es avanzar un proyecto, no resolver una consulta suelta. La quincenal es el punto de entrada; semanal e intensivo aumentan el ritmo, no cambian el modelo."
                    : "Ongoing support: the same judgement every 15 days or every week, with continuity between sessions. Best when the goal is moving a project forward, not solving a one-off question. Biweekly is the entry point; weekly and intensive raise the pace, not the model."}
                />
              </p>
              <div className="mt-2 space-y-2">
                {plans.map((p) => (
                  <div key={p.key}>
                    <button
                      type="button"
                      onClick={() => setUserPick(p.key)}
                      aria-pressed={selected === p.key}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-2xl border p-3 text-left transition-colors",
                        selected === p.key
                          ? "border-primary bg-primary"
                          : "border-border bg-card hover:bg-secondary/60",
                      )}
                    >
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 font-body text-sm font-semibold text-foreground">
                          {p.name}
                          {activeTier === p.key && (
                            <span className="inline-flex items-center gap-0.5 font-body text-[10px] font-semibold text-foreground/70">
                              <Check className="h-3 w-3" aria-hidden="true" strokeWidth={2} />
                              {t("expertSupport", "yourPlan")}
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 truncate font-body text-[11px] text-muted-foreground">{p.cadence}</p>
                      </div>
                      <div className="flex shrink-0 items-baseline gap-0.5">
                        <span className="font-figures tabular-nums text-xl font-bold text-foreground">{p.price}</span>
                        <span className="font-body text-[10px] text-muted-foreground">{p.period}</span>
                      </div>
                    </button>
                    {selected === p.key && (
                      <Button
                        size="sm"
                        className="mt-2 w-full gap-2 rounded-full font-semibold"
                        disabled={subscribing !== null}
                        onClick={() => subscribeTier(p.key)}
                      >
                        {subscribing === p.key && (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        )}
                        {t("expertSupport", "startAccompaniment")}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Book a session — only rendered when hours are available */}
            {canBook && (
              <div className="pt-1">
                <Button
                  className="w-full gap-2 rounded-full font-semibold"
                  onClick={() => {
                    if (onBook) {
                      onBook();
                      onOpenChange(false);
                    } else {
                      setView("booking");
                    }
                  }}
                >
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
                  {t("expertSupport", "bookSession")}
                </Button>
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default VipMentoringPanel;
